import {useCallback} from 'react';
import useUpdatedRef from 'src/public/useUpdatedRef/useUpdatedRef';

type AnyFunction = (...args: any[]) => any;

/*
 * Use this when the `callback` prop changes often but you do not want to re-run effect hooks every time `callback` changes.
 * Instead, you want to limit the execution to changes in some other variables or props.
 *
 * The function creates a mutable ref storing the current value of the callback function. The ref is updated
 * if the callback changes, within the layout effect run on every render before the DOM is updated, and hence
 * always reflects the current value of the `callback` prop.
 */
export default function useStableCallback<T extends AnyFunction>(
  callback: T,
): T {
  const callbackRef = useUpdatedRef(callback);

  // Return a stable function that always invokes the latest callback
  return useCallback(
    (...args: Parameters<T>) => callbackRef.current?.(...args),
    // We know that "callbackRef" is stable and could be excluded. But we include it in the dependency array here for correctness.
    [callbackRef],
  ) as T;
}
